/**
 * Organisations + invitations API. Mirrors ``backend/orgs/router.py`` and
 * ``backend/invitations/router.py``.
 *
 * ``orgsApi`` is admin-side: org CRUD, member roles, invitation lifecycle.
 * ``invitationsApi`` is the unauthenticated invite-recipient surface: preview
 * + accept.
 */
import apiClient from "@lib/axios";
import type {
  AcceptInvitationResponse,
  CreateInvitationRequest,
  CreateOrgRequest,
  Invitation,
  InvitationPreview,
  OrgRole,
  Organisation,
  OrganisationMember,
  UpdateOrgRequest,
} from "@models/organisation";
import { OrganisationMemberSchema } from "@services/schemas";
import { z } from "zod";

export const orgsApi = {
  list: async (): Promise<Organisation[]> => {
    const { data } = await apiClient.get<Organisation[]>("/orgs");
    return data;
  },
  create: async (body: CreateOrgRequest): Promise<Organisation> => {
    const { data } = await apiClient.post<Organisation>("/orgs", body);
    return data;
  },
  get: async (orgId: string): Promise<Organisation> => {
    const { data } = await apiClient.get<Organisation>(
      `/orgs/${encodeURIComponent(orgId)}`,
    );
    return data;
  },
  update: async (
    orgId: string,
    body: UpdateOrgRequest,
  ): Promise<Organisation> => {
    const { data } = await apiClient.patch<Organisation>(
      `/orgs/${encodeURIComponent(orgId)}`,
      body,
    );
    return data;
  },
  delete: async (orgId: string): Promise<void> => {
    await apiClient.delete(`/orgs/${encodeURIComponent(orgId)}`);
  },
  listMembers: async (orgId: string): Promise<OrganisationMember[]> => {
    const { data } = await apiClient.get(
      `/orgs/${encodeURIComponent(orgId)}/members`,
    );
    return z.array(OrganisationMemberSchema).parse(data);
  },

  // ── Member management (Phase 1b) ──────────────────────────────────────────

  updateMemberRole: async (
    orgId: string,
    targetUserId: string,
    role: OrgRole,
  ): Promise<OrganisationMember> => {
    const { data } = await apiClient.patch(
      `/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(targetUserId)}`,
      { role },
    );
    return OrganisationMemberSchema.parse(data);
  },

  removeMember: async (orgId: string, targetUserId: string): Promise<void> => {
    await apiClient.delete(
      `/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(targetUserId)}`,
    );
  },

  // ── Invitations — admin surface (Phase 1b) ────────────────────────────────

  listInvitations: async (orgId: string): Promise<Invitation[]> => {
    const { data } = await apiClient.get<Invitation[]>(
      `/orgs/${encodeURIComponent(orgId)}/invitations`,
    );
    return data;
  },

  createInvitation: async (
    orgId: string,
    body: CreateInvitationRequest,
  ): Promise<Invitation> => {
    const { data } = await apiClient.post<Invitation>(
      `/orgs/${encodeURIComponent(orgId)}/invitations`,
      body,
    );
    return data;
  },

  revokeInvitation: async (
    orgId: string,
    invitationId: string,
  ): Promise<void> => {
    await apiClient.delete(
      `/orgs/${encodeURIComponent(orgId)}/invitations/${encodeURIComponent(invitationId)}`,
    );
  },
};

export const invitationsApi = {
  preview: async (token: string): Promise<InvitationPreview> => {
    const { data } = await apiClient.get<InvitationPreview>(
      `/invitations/${encodeURIComponent(token)}`,
    );
    return data;
  },

  accept: async (token: string): Promise<AcceptInvitationResponse> => {
    const { data } = await apiClient.post<AcceptInvitationResponse>(
      `/invitations/${encodeURIComponent(token)}/accept`,
    );
    return data;
  },
};
